import CancelButton from 'components/buttons/CancelButton';
import PermissionButton from 'components/buttons/PermissionButton';
import Loader from 'components/Loader';
import ConfirmNavigation from 'components/navigation/ConfirmNavigation';
import PageHeader from 'components/PageHeader';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'store';
import { useGetCorporateRoleFillingListQuery, useUpdateCorporateRoleFillingsMutation } from 'store/api/companies';
import {
    CorporateRoleFilling,
    CorporateRoleFillingForm,
    CorporateRoleType,
    UpdateCorporateRoleFillingDto,
} from 'types/companies';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import CorporateRoleFillingsTable from './CorporateRoleFillingsTable';

export type CorporateRoleFillingRowType = {
    name: string;
    level: number;
    leadingRevenuePersonIndex?: number;
    leadingCompliancePersonIndex?: number;
};

function mapCorporateRoleFillingsToForm(list: CorporateRoleFilling[]) {
    return list.map((item) => ({
        corporateRoleFillingId: item.corporateRoleFillingId,
        roleFilledByEmployee: item.roleFilledByEmployee ?? null,
    }));
}

const CorporateRolePage = (): JSX.Element => {
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const { buttonsDisabled: areButtonsDisabled } = useSelector((state) => state.buttons);
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: ['CORPORATE_ROLE_TYPE'],
    });

    const { data: corporateRoleFillingList, refetch, isLoading: isLoadingData } = useGetCorporateRoleFillingListQuery();
    const [updateCorporateRoleFillings] = useUpdateCorporateRoleFillingsMutation();

    const initialData = useMemo<CorporateRoleFillingForm | undefined>(() => {
        if (!corporateRoleFillingList) return undefined;
        const corporateRoleFillings: UpdateCorporateRoleFillingDto[] =
            mapCorporateRoleFillingsToForm(corporateRoleFillingList);
        return { corporateRoleFillings };
    }, [corporateRoleFillingList]);

    const dataToDisplay = useMemo<CorporateRoleFillingRowType[]>(() => {
        if (!corporateRoleFillingList) return [];
        return corporateRoleFillingList
            .filter((corporateRole) => corporateRole.corporateRoleTypeCode === CorporateRoleType.LEADING_REVENUE_PERSON)
            .map((leadingRevenuePersonFilling) => {
                const leadingCompliancePersonFilling = corporateRoleFillingList.find(
                    (el) =>
                        el.corporateRoleTypeCode === CorporateRoleType.LEADING_COMPLIANCE_PERSON &&
                        el.name === leadingRevenuePersonFilling.name &&
                        el.level === leadingRevenuePersonFilling.level &&
                        el.scopeTypeCode === leadingRevenuePersonFilling.scopeTypeCode &&
                        el.company?.id === leadingRevenuePersonFilling.company?.id &&
                        el.office?.id === leadingRevenuePersonFilling.office?.id
                );
                return {
                    name: leadingRevenuePersonFilling.name,
                    level: leadingRevenuePersonFilling.level,
                    leadingRevenuePersonIndex: corporateRoleFillingList.indexOf(leadingRevenuePersonFilling),
                    leadingCompliancePersonIndex: leadingCompliancePersonFilling
                        ? corporateRoleFillingList.indexOf(leadingCompliancePersonFilling)
                        : undefined,
                };
            });
    }, [corporateRoleFillingList]);

    const { handleSubmit, control, setValue, reset } = useForm<CorporateRoleFillingForm>({
        defaultValues: {
            corporateRoleFillings: [],
        },
        values: initialData,
    });

    const onSubmit = async (formData: CorporateRoleFillingForm): Promise<void> => {
        try {
            await updateCorporateRoleFillings(formData.corporateRoleFillings)
                .unwrap()
                .then(() => {
                    reset({}, { keepValues: true });
                    setIsEditing(false);
                })
                .catch(console.error);
        } catch (e) {
            console.error('onSubmit error: ', e);
            errorAlert(t('forms.error-submitting-the-form'));
        }
    };

    const submitUpdateCorporateRoleFillings = () => {
        handleSubmit((data) => onSubmit(data))();
    };

    if (isLoadingData || initialData === undefined || isLoadingClassifiers) {
        return <Loader />;
    }

    return (
        <PaddedLayout className="overflow-hidden">
            <div className="flex h-full flex-col gap-4">
                <PageHeader
                    titleId={'corporate-role.title'}
                    buttons={
                        isEditing ? (
                            <div className="flex gap-2">
                                <CancelButton
                                    onClick={() => {
                                        // reset either clears the entire form or leaves new unsaved values
                                        refetch();
                                        setIsEditing(false);
                                    }}
                                    disabled={areButtonsDisabled}
                                />
                                <div>
                                    <PermissionButton
                                        permissions={['UPDATE.COMPANIES']}
                                        variant="contained"
                                        color="secondary"
                                        size="small"
                                        onClick={submitUpdateCorporateRoleFillings}
                                        disabled={areButtonsDisabled}
                                    >
                                        <FormattedMessage id="save" />
                                    </PermissionButton>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <PermissionButton
                                    permissions={['UPDATE.COMPANIES']}
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setIsEditing(true)}
                                    disabled={areButtonsDisabled}
                                >
                                    <FormattedMessage id="edit" />
                                </PermissionButton>
                            </div>
                        )
                    }
                />
                <CorporateRoleFillingsTable
                    classifiers={classifiers}
                    control={control}
                    setValue={setValue}
                    isReadOnly={!isEditing}
                    dataToDisplay={dataToDisplay}
                />
                <ConfirmNavigation control={control} />
            </div>
        </PaddedLayout>
    );
};

export default CorporateRolePage;
